import { readEnv } from "@repo/helpers";

export const B2CPolicies = {
    // A statically-configured policy name (VITE_/GATSBY_B2C_SIGNUP_SIGNIN_POLICY, e.g. a Library
    // variable) is preferred over the computed policy, but only when LocalAccount MFA is off —
    // MFA must always win, since the override policy doesn't support it. This keeps both
    // feature-flag decisions (MFA + V2 registration) and the override lookup in one place.
    DirectLogin: () => {
        // LocalAccount MFA is only ever applied on top of the V2 registration flow — both flags
        // must be enabled, not just the MFA one.
        if (isLocalAccountMfaFeatureEnabled() && isV2RegistrationFeatureEnabled()) {
            return "B2C_1A_LOGINPOLICYMFA";
        }
        const staticOverridePolicy = getStaticLoginPolicyOverride();
        if (staticOverridePolicy) {
            return staticOverridePolicy;
        }
        return isV2RegistrationFeatureEnabled() ? "B2C_1A_LOGINPOLICYV2" : "B2C_1A_LOGINPOLICY";
    },
    DirectRegistration: () => {
        return isV2RegistrationFeatureEnabled()
            ? "B2C_1A_DIRECTREGISTRATIONPOLICYV2"
            : "B2C_1A_DIRECTREGISTRATIONPOLICY";
    },
    BankIdLogin: () => {
        return isV2RegistrationFeatureEnabled() ? "B2C_1A_BANKIDLOGINPOLICYV2" : "B2C_1A_BANKIDLOGINPOLICY";
    },
    PartnerPolicy: () => {
        return isV2RegistrationFeatureEnabled() ? "B2C_1A_PARTNERPOLICYV2" : "B2C_1A_PARTNERPOLICY";
    },
    ImpersonationPolicy: () => {
        return isV2RegistrationFeatureEnabled() ? "B2C_1A_IMPERSONATIONPOLICYV2" : "B2C_1A_IMPERSONATIONPOLICY";
    },
    PhoneChangePolicy: () => "B2C_1A_PHONE_CHANGE_POLICY" as const,
    EmailChangePolicy: () => "B2C_1A_EMAIL_CHANGE_POLICY" as const,
    PrototypeLogin: () => "B2C_1_PrototypeLogin" as const,
    PasswordReset: () => {
        return isLocalAccountPasswordResetAuthorizationFeatureEnabled() && isV2RegistrationFeatureEnabled()
            ? "B2C_1A_PASSWORD_RESET_AUTHORIZATION"
            : (readEnv("VITE_B2C_SIGNUP_RESET_PASSWORD_POLICY") ?? "B2C_1A_PASSWORD_RESET");
    },
} as const;

export type B2CPolicy = ReturnType<(typeof B2CPolicies)[keyof typeof B2CPolicies]>;

function isV2RegistrationFeatureEnabled() {
    return readEnv("VITE_FEATURE_IS_V2_REGISTRATION_ENABLED") === "true";
}

function isLocalAccountMfaFeatureEnabled() {
    return readEnv("VITE_FEATURE_IS_LOCAL_ACCOUNT_MFA_ENABLED") === "true";
}

function isLocalAccountPasswordResetAuthorizationFeatureEnabled() {
    return readEnv("VITE_FEATURE_IS_LOCAL_ACCOUNT_PASSWORD_RESET_AUTHORIZATION_ENABLED") === "true";
}

function getStaticLoginPolicyOverride() {
    return readEnv("VITE_B2C_SIGNUP_SIGNIN_POLICY");
}
